import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Link2, UserCog, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function heightLabel(h) {
  if (h === "4/5") return "4/5";
  if (h === "full") return "Full";
  return `${h.replace("/4", "")}/4`;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState("");

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/p/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      el.remove();
    }
    setCopiedSlug(slug);
    toast.success("Link copied");
    window.setTimeout(() => setCopiedSlug((s) => (s === slug ? "" : s)), 1500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pages");
      setPages(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/pages/${id}`);
      toast.success("Page deleted");
      setPages((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/dashboard"
            className="text-sm font-bold uppercase tracking-[0.28em] text-zinc-900"
            data-testid="dashboard-brand"
          >
            Form Wrapper
          </Link>
          <div className="flex items-center gap-3">
            <span
              className="hidden sm:inline text-xs text-zinc-500 uppercase tracking-[0.2em]"
              data-testid="dashboard-user-email"
            >
              {user?.email}
            </span>
            <Button
              variant="ghost"
              onClick={() => navigate("/account")}
              data-testid="dashboard-account-btn"
              className="text-zinc-700 hover:text-zinc-900"
            >
              <UserCog className="w-4 h-4 mr-2" /> Account
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="dashboard-logout-btn"
              className="text-zinc-700 hover:text-zinc-900"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Landing page card */}
        <Link
          to="/landing"
          className="block mb-10 rounded-2xl border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 transition-colors p-6 sm:p-8"
          data-testid="dashboard-landing-card"
        >
          <div className="flex items-center justify-between gap-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.28em] text-zinc-500 mb-2">
                Landing
              </div>
              <h2
                className="text-2xl sm:text-3xl font-black tracking-tighter text-zinc-900 leading-tight"
                style={{ fontFamily: "var(--font-cabinet), Cabinet Grotesk, sans-serif" }}
              >
                Landing page
              </h2>
              <p className="text-sm text-zinc-600 mt-1 max-w-md">
                Customize the page visitors see at <span className="font-mono">/</span> —
                heading, buttons, and the “Go to Website” link.
              </p>
            </div>
            <div className="text-2xl text-zinc-400">›</div>
          </div>
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.28em] text-zinc-500 mb-3">
              Console
            </div>
            <h1
              className="text-4xl sm:text-5xl font-black tracking-tighter text-zinc-900 leading-[0.95]"
              style={{ fontFamily: "var(--font-cabinet), Cabinet Grotesk, sans-serif" }}
              data-testid="dashboard-heading"
            >
              Your wrapped pages.
            </h1>
            <p className="mt-3 text-zinc-600 max-w-xl">
              Every page slides a third-party form up over your branded background.
              Edit the slug, color, height, or target URL anytime.
            </p>
          </div>
          <Button
            onClick={() => navigate("/pages/new")}
            className="h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold tracking-wide self-start sm:self-auto"
            data-testid="dashboard-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New page
          </Button>
        </div>

        {loading ? (
          <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]" data-testid="dashboard-loading">
            Loading…
          </div>
        ) : pages.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-dashed border-zinc-300 rounded-lg p-12 text-center"
            data-testid="dashboard-empty"
          >
            <div className="text-zinc-900 font-semibold text-xl mb-2">No pages yet</div>
            <p className="text-zinc-500 mb-6 max-w-md mx-auto">
              Create your first wrapper to display any third-party form under your own domain.
            </p>
            <Button
              onClick={() => navigate("/pages/new")}
              className="h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="dashboard-empty-create-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Create your first page
            </Button>
          </motion.div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="dashboard-pages-grid"
          >
            {pages.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.35 }}
                className="group border border-zinc-200 rounded-lg bg-white p-6 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                data-testid={`page-card-${p.slug}`}
              >
                <div
                  className="h-20 w-full rounded-md mb-5 border border-zinc-200"
                  style={{ backgroundColor: p.bg_color }}
                  aria-label={`Background ${p.bg_color}`}
                />
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  <Link2 className="w-3.5 h-3.5" />
                  /p/{p.slug}
                </div>
                <div
                  className="text-lg font-bold tracking-tight text-zinc-900 truncate"
                  title={p.title || p.target_url}
                >
                  {p.title || p.target_url}
                </div>
                <div className="mt-1 text-xs text-zinc-500 truncate" title={p.target_url}>
                  {p.target_url}
                </div>
                <div className="mt-5 flex items-center justify-between text-xs text-zinc-500">
                  <span className="uppercase tracking-[0.2em]">
                    Height · {heightLabel(p.popup_height)}
                  </span>
                  <span className="font-mono">{p.bg_color.toUpperCase()}</span>
                </div>
                <div className="mt-6 flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 h-10 rounded-md border-zinc-300"
                    onClick={() => navigate(`/pages/${p.id}`)}
                    data-testid={`page-edit-${p.slug}`}
                  >
                    <Pencil className="w-4 h-4 mr-1.5" /> Edit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 rounded-md border-zinc-300"
                    onClick={() => copyLink(p.slug)}
                    data-testid={`page-copy-${p.slug}`}
                    aria-label="Copy public link"
                  >
                    {copiedSlug === p.slug ? (
                      <>
                        <Check className="w-4 h-4 mr-1.5" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1.5" /> Copy Link
                      </>
                    )}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-10 rounded-md border-zinc-300 text-red-600 hover:text-red-700"
                        data-testid={`page-delete-${p.slug}`}
                        aria-label="Delete page"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this page?</AlertDialogTitle>
                        <AlertDialogDescription>
                          The slug <span className="font-mono">/p/{p.slug}</span> will stop
                          resolving. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`page-delete-cancel-${p.slug}`}>
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(p.id)}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid={`page-delete-confirm-${p.slug}`}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
